/* ═══════════════════════════════════════════════════════════════════════════
   🌍 WorldMap.jsx — the world map in the browser: a REAL map (MapLibre GL with
   OpenStreetMap data from OpenFreeMap — no key, no bill), shown as a globe when
   far out and a street map close in. The same rules (worldMap.js, generated
   from the app) and the same server (mapRoutes.js) as the phone, whose twin is
   src/components/worldmap/*.

     <WorldMapCard>  the door, at the top of the Vibes left pane — the real map
                     around you with your sky washed over it
     <WorldMap>      full screen: the map, Allo on it (counts far out; public
                     people, neighbourhoods and pages close in; friends by face),
                     your weather over it, the card behind a dot, "who sees me"

   🟢 THE DOTS ARE MAP LAYERS, NOT ELEMENTS: counts, neighbourhoods, people and
      pages are GeoJSON drawn by the map's own GPU renderer — thousands cost what
      ten do. Only friends and I are HTML markers, because they carry faces.
   🟢 THE MAP'S OWN SKY IS THE VIEWER'S: far out, the globe's atmosphere takes
      the colours of the sky where you are; at night the map goes dark.
   🟥 THE MAP IS MADE WHEN THE PANE OPENS AND REMOVED WHEN IT CLOSES — a WebGL
      map left alive behind a closed overlay is memory and battery spent on
      nothing.
   ═══════════════════════════════════════════════════════════════════════════ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  jitterInCell, MAP_CITY_ZOOM, MAP_GREEN, MAP_HOME_ZOOM, MAP_OFFLINE, MAP_ONLINE_CHOICES, MAP_START_ZOOM, MAP_VISIBILITIES, MAP_WORLD_ZOOM, diveFrom,
  compactCount, distanceKm, dotColor, fallbackPhase, formatTemp, usesFahrenheit, isNameLabel, layerForZoom, mapL, mapNameField, mergeCells, nearPrefixes, pageKind, placeLabel,
  precisionForLayer, skyPhase, skyScene, spreadAround, sunAt, weatherKind, cityLightLevel, cityLightLayers, CITY_LIGHT_IDS, placeLabelPaint, mapErrorKey,
} from '../services/worldMap';
import {
  cachedCells, chooseCity, fetchFriendsOnMap, fetchMe, fetchNear, fetchWeather, fetchWho, loadCells, placeFromProfileCity, refreshAfterSetting,
  rememberLook, rememberedLook, searchCities, setAsPage, fetchPageView,
} from '../services/worldMapApi';
import { sendFriendRequest } from '../services/vibes';
import { toggleFollow as toggleFollowPage } from '../services/pageFollow';
import './worldmap.css';

const lang0 = () => (typeof navigator !== 'undefined' ? navigator.language : 'en');

/* 🗺 OpenFreeMap: OpenStreetMap vector tiles, free and keyless. Day is "liberty";
   night is "dark" — and if that style ever fails to load, the day map is used
   rather than no map. */
export const MAP_STYLE_DAY = 'https://tiles.openfreemap.org/styles/liberty';
export const MAP_STYLE_NIGHT = 'https://tiles.openfreemap.org/styles/dark';
const FONT = ['Noto Sans Bold'];

/* 🟥 ARABIC NEEDS THE RTL PLUGIN. Without it MapLibre draws right-to-left names letter by
   letter, unjoined and backwards — "مصر" comes out as "ر ص م". Loaded once, lazily
   (only when a right-to-left name is on screen), from a second CDN if the first fails. */
const RTL_PLUGIN = [
  'https://cdn.jsdelivr.net/npm/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js',
  'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js',
];
function ensureRtl() {
  try {
    if (maplibregl.getRTLTextPluginStatus() !== 'unavailable') return;
    const p = maplibregl.setRTLTextPlugin(RTL_PLUGIN[0], true);
    if (p && p.catch) p.catch(() => { try { maplibregl.setRTLTextPlugin(RTL_PLUGIN[1], true); } catch { /* names stay as drawn */ } });
  } catch { /* names stay as drawn */ }
}
/** Place names in the reader's language: "مصر" for an Arabic reader, "Egypt" for an English one. */
function localizeNames(map, lang) {
  const st = map.getStyle(); if (!st || !st.layers) return;
  const field = mapNameField(lang);
  for (const l of st.layers) {
    if (l.type !== 'symbol' || !l.layout || !l.layout['text-field'] || l.id.startsWith('wm-')) continue;
    if (!isNameLabel(l.layout['text-field'])) continue;
    try { map.setLayoutProperty(l.id, 'text-field', field); } catch { /* the style's own name */ }
  }
}

/** 🌃 The cities' lights (under every name and every dot) and brighter, haloed place
    names — the same layers the phones draw (worldMap.cityLightLayers). */
function applyLights(map, level, dark) {
  const st = map.getStyle(); if (!st || !st.layers) return;
  for (const id of CITY_LIGHT_IDS) if (map.getLayer(id)) { try { map.removeLayer(id); } catch { /* gone */ } }
  const src = Object.keys(st.sources || {}).find((k) => st.sources[k] && st.sources[k].type === 'vector');
  const before = (st.layers.find((l) => l.type === 'symbol') || {}).id;
  if (src) for (const l of cityLightLayers(level, src)) { try { map.addLayer(l, before); } catch { /* a style without that data */ } }
  const paint = placeLabelPaint(dark);
  for (const l of st.layers) {
    if (l.type !== 'symbol' || l['source-layer'] !== 'place' || l.id.startsWith('wm-')) continue;
    for (const k of Object.keys(paint)) { try { map.setPaintProperty(l.id, k, paint[k]); } catch { /* keeps its own */ } }
  }
}

const HOUR_TINT = {
  sunrise: 'rgba(255,170,110,0.13)', sunset: 'rgba(255,120,80,0.15)',
  dawn: 'rgba(120,90,190,0.13)', dusk: 'rgba(110,70,160,0.15)', night: 'rgba(6,14,40,0.12)',
};

/* ══ THE SKY ═════════════════════════════════════════════════════════════
   One canvas, with some cinema in it (the phone's twin is WeatherSky.tsx):
     light   a low sun's glow that breathes, god rays turning slowly out of it
     night   stars in three depths twinkling out of step, a few with a glint,
             a shooting star now and then, the moon with a halo, a vignette
     clouds  two layers at two speeds; over a map, their shadows on the ground
     rain    three depths at three speeds, slanted by the wind, splashes, haze
     snow    fine far flakes, flakes, and big soft out-of-focus ones, swaying
     fog     two banks rolling past each other
     storm   a bolt, a flash and a flicker, at uneven intervals
   `overlay` (over a real map): the sky's own things keep to the band at the
   top; the weather falls over everything; the hour's light washes the map.
   A new sky fades in over the old one; "reduce motion" stills it. */
function scatter(n, seed) {
  let s = seed >>> 0;
  const r = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  return Array.from({ length: n }, () => ({ x: r(), y: r(), a: r(), b: r() }));
}

function makeBolt(W, H) {
  const pts = []; let x = W * (0.25 + Math.random() * 0.5); let y = 0;
  pts.push([x, y]);
  for (let i = 0; i < 10; i++) { x += (Math.random() - 0.5) * W * 0.09; y += H * 0.055 * (0.7 + Math.random() * 0.6); pts.push([x, y]); }
  const at = pts[4]; const fork = [[at[0], at[1]]];
  let fx = at[0]; let fy = at[1];
  for (let i = 0; i < 4; i++) { fx += W * (0.025 + Math.random() * 0.03); fy += H * 0.04; fork.push([fx, fy]); }
  return { pts, fork };
}

function SkyCanvas({ scene, running, overlay = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !running) return undefined;
    const ctx = c.getContext('2d');
    const still = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stars = scatter(170, 7); const bright = scatter(7, 41); const clouds = scatter(9, 3); const splashes = scatter(14, 71);
    const far = scatter(scene.particles === 'snow' ? 90 : 130, 31); const mid = scatter(scene.particles === 'snow' ? 55 : 80, 57); const near = scatter(scene.particles === 'snow' ? 12 : 30, 83);
    const warm = scene.phase === 'sunrise' || scene.phase === 'sunset';
    const night = scene.phase === 'night' || scene.phase === 'dusk' || scene.phase === 'dawn';
    const sunCol = scene.phase === 'day' ? '255,246,214' : scene.phase === 'sunrise' ? '255,208,138' : '255,176,112';
    const cloudTint = night ? '142,154,184' : scene.lightning ? '154,163,178' : '255,255,255';
    const wind = scene.lightning ? 0.32 : 0.18;
    const t0 = performance.now();
    let raf = 0; let flash = 0; let flick = false; let boltA = 0; let bolt = null;
    let nextStrike = t0 + 2200; let nextShot = t0 + 3500; let shot = null;
    const glowAt = (x, y, r, col, a) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(${col},${a})`); g.addColorStop(1, `rgba(${col},0)`);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    };
    const draw = (now) => {
      const t = still ? 0 : now;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const W = c.clientWidth; const H = c.clientHeight;
      if (c.width !== Math.round(W * dpr)) { c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.globalAlpha = Math.min(1, (now - t0) / 1100);          // a new sky fades in
      const skyH = overlay ? Math.min(H * 0.24, 230) : H;
      const sunX = scene.phase === 'sunrise' || scene.phase === 'dawn' ? W * 0.18 : W * 0.82;
      const sunY = overlay ? -skyH * 0.15 : scene.phase === 'day' ? H * 0.15 : H * 0.62;
      const base = ctx.globalAlpha;

      if (overlay && HOUR_TINT[scene.phase]) { ctx.fillStyle = HOUR_TINT[scene.phase]; ctx.fillRect(0, 0, W, H); }

      /* 🌅 the glow, breathing, and god rays turning out of it */
      if (scene.sun > 0 || warm) {
        const br = 0.85 + 0.15 * Math.sin(t / 1650);
        glowAt(sunX, sunY, Math.max(W, skyH) * (warm ? 0.95 : 0.6), sunCol, (warm ? 0.5 : 0.28) * Math.max(0.35, scene.sun) * br);
        if (scene.clouds < 0.85) {
          ctx.save(); ctx.translate(sunX, sunY); ctx.rotate(Math.sin(t / 6700) * 0.12);
          const R = Math.max(W, H) * 1.1;
          const g = ctx.createRadialGradient(0, 0, 0, 0, 0, R);
          g.addColorStop(0, `rgba(${sunCol},${(warm ? 0.075 : 0.035) * br})`); g.addColorStop(0.55, `rgba(${sunCol},${(warm ? 0.025 : 0.01) * br})`); g.addColorStop(1, `rgba(${sunCol},0)`);
          ctx.fillStyle = g;
          for (let i = 0; i < 13; i++) {
            /* each shaft is three nested wedges — a soft edge without a blur filter */
            const mid = (i / 13) * Math.PI * 2 + 0.2 + (i % 2) * 0.05; const w = 0.035 + (i % 3) * 0.018;
            for (const f of [1, 0.62, 0.3]) {
              ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.cos(mid - w * f) * R, Math.sin(mid - w * f) * R); ctx.lineTo(Math.cos(mid + w * f) * R, Math.sin(mid + w * f) * R); ctx.closePath(); ctx.fill();
            }
          }
          ctx.restore();
        }
      }

      /* ✨ stars, three depths, out of step — and a few with a glint */
      if (scene.stars > 0) {
        for (let i = 0; i < stars.length; i++) {
          const s = stars[i]; const g = i % 3;
          const tw = 0.55 + 0.45 * Math.sin(t / (1500 + g * 650) + i * 1.7);
          ctx.globalAlpha = base * scene.stars * (0.35 + s.b * 0.65) * tw;
          ctx.fillStyle = s.b > 0.85 ? '#FFE9C9' : s.b < 0.12 ? '#CFE0FF' : '#FFFFFF';
          ctx.beginPath(); ctx.arc(s.x * W, s.y * skyH * (overlay ? 0.9 : 0.75), 0.45 + s.a * (g === 2 ? 1.5 : 1.1), 0, Math.PI * 2); ctx.fill();
        }
        ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 0.6;
        for (const s of bright) {
          const x = s.x * W; const y = s.y * skyH * 0.6; const tw = 0.6 + 0.4 * Math.sin(t / 900 + s.a * 9);
          ctx.globalAlpha = base * scene.stars * tw;
          ctx.beginPath(); ctx.moveTo(x - 7, y); ctx.lineTo(x + 7, y); ctx.moveTo(x, y - 7); ctx.lineTo(x, y + 7); ctx.stroke();
          ctx.beginPath(); ctx.arc(x, y, 1.6, 0, Math.PI * 2); ctx.fillStyle = '#FFFFFF'; ctx.fill();
        }
        /* 🌠 now and then, a shooting star */
        if (!still && scene.stars > 0.3) {
          if (!shot && now > nextShot) { shot = { x: Math.random() * W * 0.6, y: Math.random() * skyH * 0.35, at: now }; nextShot = now + 6000 + Math.random() * 9000; }
          if (shot) {
            const k = (now - shot.at) / 1100;
            if (k >= 1) shot = null;
            else {
              const len = 120; const dx = Math.cos(0.42); const dy = Math.sin(0.42);
              const hx = shot.x + dx * W * 0.45 * k; const hy = shot.y + dy * W * 0.45 * k;
              const g = ctx.createLinearGradient(hx - dx * len, hy - dy * len, hx, hy);
              g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(255,255,255,0.95)');
              ctx.globalAlpha = base * (k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85);
              ctx.strokeStyle = g; ctx.lineWidth = 1.6; ctx.beginPath(); ctx.moveTo(hx - dx * len, hy - dy * len); ctx.lineTo(hx, hy); ctx.stroke();
            }
          }
        }
        ctx.globalAlpha = base;
      }

      /* ☀️ / 🌙 — the sun's disc behind a globe only; the moon keeps to the band over a map */
      if (!overlay && scene.sun > 0) {
        glowAt(sunX, sunY, 230, sunCol, 0.55 * scene.sun);
        ctx.globalAlpha = base * 0.95 * scene.sun; ctx.fillStyle = `rgb(${sunCol})`; ctx.beginPath(); ctx.arc(sunX, sunY, 52, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = base;
      } else if (scene.moon > 0) {
        const x = W * 0.8; const y = overlay ? skyH * 0.32 : H * 0.14; const r = overlay ? 20 : 30;
        ctx.save();
        ctx.globalAlpha = base * 0.95 * scene.moon; ctx.fillStyle = '#F4F1E6'; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        ctx.globalCompositeOperation = 'destination-out'; ctx.globalAlpha = 1;
        ctx.beginPath(); ctx.arc(x + r * 0.42, y - r * 0.16, r * 0.84, 0, Math.PI * 2); ctx.fill();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.globalAlpha = base; glowAt(x, y, r * 5, '221,230,255', 0.3 * scene.moon);
        ctx.restore();
      }

      /* ☁️ two layers at two speeds — and over a map, their shadows on the ground */
      if (scene.clouds > 0) {
        for (let layer = 0; layer < 2; layer++) {
          const speed = layer ? 85000 : 160000;
          for (let i = 0; i < clouds.length; i++) {
            const k = clouds[i];
            const x = (((k.x * 2.2 + t / speed) % 2.2) - 0.6) * W;
            const y = skyH * ((layer ? 0.12 : 0.06) + k.y * (overlay ? 0.55 : 0.32));
            const w = W * (layer ? 0.55 + k.a * 0.4 : 0.32 + k.a * 0.25);
            const a = (layer ? 0.42 : 0.3) * scene.clouds * (overlay ? 0.75 : 1);
            for (const [ox, oy, rx, ry] of [[0, 0, 0.55, 0.2], [0.14, -0.08, 0.3, 0.17], [-0.18, -0.04, 0.25, 0.13]]) {
              ctx.save(); ctx.translate(x + w * ox, y + w * oy); ctx.scale(1, ry / rx);
              const g = ctx.createRadialGradient(0, 0, 0, 0, 0, w * rx);
              g.addColorStop(0, `rgba(${cloudTint},${a})`); g.addColorStop(0.65, `rgba(${cloudTint},${a * 0.42})`); g.addColorStop(1, `rgba(${cloudTint},0)`);
              ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, w * rx, 0, Math.PI * 2); ctx.fill(); ctx.restore();
            }
          }
        }
        if (overlay && scene.clouds > 0.3) {
          for (let i = 0; i < 5; i++) {
            const k = clouds[i];
            const x = (((k.x * 2 + t / 85000) % 2) - 0.5) * W; const y = H * (0.3 + k.b * 0.6); const rx = W * (0.35 + k.a * 0.3);
            ctx.save(); ctx.translate(x, y); ctx.scale(1, 0.5);
            const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
            g.addColorStop(0, `rgba(11,18,34,${0.16 * scene.clouds})`); g.addColorStop(1, 'rgba(11,18,34,0)');
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, rx, 0, Math.PI * 2); ctx.fill(); ctx.restore();
          }
        }
      }

      /* 🌧 three depths, three speeds, slanted — and splashes where it lands */
      if (scene.particles === 'rain') {
        const dens = Math.max(0.35, scene.density);
        [[far, 980, 9, 0.7, 0.22], [mid, 680, 15, 1, 0.34], [near, 470, 26, 1.6, 0.42]].forEach(([arr, ms, len, lw, al], depth) => {
          ctx.strokeStyle = '#DCE8FF'; ctx.lineWidth = lw; ctx.lineCap = 'round';
          const n = Math.round(arr.length * dens);
          const sp = (scene.lightning ? 0.75 : 1) * ms;
          for (let i = 0; i < n; i++) {
            const d = arr[i];
            const y = ((d.y + t / sp) % 1) * (H + 60) - 30; const x = d.x * (W + 120) - 60 + y * wind;
            const L = len + d.a * [5, 9, 14][depth];
            ctx.globalAlpha = base * (al + d.b * 0.2);
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + L * wind, y + L); ctx.stroke();
          }
        });
        ctx.strokeStyle = 'rgba(220,232,255,0.6)'; ctx.lineWidth = 1;
        for (let i = 0; i < splashes.length; i++) {
          const s = splashes[i]; const k = ((t / 900) + i / splashes.length) % 1;
          ctx.globalAlpha = base * (1 - k) * 0.8;
          ctx.beginPath(); ctx.ellipse(s.x * W, H * (0.55 + s.y * 0.42), 2 + k * 12, 0.8 + k * 3.5, 0, 0, Math.PI * 2); ctx.stroke();
        }
        const hz = ctx.createLinearGradient(0, H * 0.45, 0, H);
        hz.addColorStop(0, 'rgba(150,162,180,0)'); hz.addColorStop(1, `rgba(150,162,180,${scene.lightning ? 0.28 : 0.18})`);
        ctx.globalAlpha = base; ctx.fillStyle = hz; ctx.fillRect(0, H * 0.45, W, H * 0.55);
      } else if (scene.particles === 'snow') {
        /* ❄️ fine and far, flakes, and big soft ones close to the lens — all swaying */
        const dens = Math.max(0.35, scene.density);
        [[far, 15000, 0.8, 0.9], [mid, 9500, 1.6, 1.8], [near, 6200, 6, 7]].forEach(([arr, ms, r0, r1], depth) => {
          const n = Math.round(arr.length * dens);
          for (let i = 0; i < n; i++) {
            const d = arr[i];
            const y = ((d.y + t / ms) % 1) * (H + 40) - 20;
            const x = d.x * W + Math.sin(t / (1700 + depth * 600) + i) * (6 + depth * 10);
            const r = r0 + d.a * r1;
            if (depth === 2) {
              ctx.globalAlpha = base * 0.55;
              glowAt(x, y, r, '255,255,255', 0.6);
            } else {
              ctx.globalAlpha = base * ((depth ? 0.7 : 0.45) + d.b * 0.25); ctx.fillStyle = '#FFFFFF';
              ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
            }
          }
        });
        ctx.globalAlpha = base;
      }

      /* 🌫 fog banks rolling past each other */
      if (scene.fog > 0) {
        for (let b = 0; b < 2; b++) {
          const shift = Math.sin(t / 14000 + b * Math.PI) * W * 0.25;
          const y = H * (b ? 0.7 : 0.47);
          for (const [cx, rx] of [[0.3, 0.7], [1.1, 0.6]]) {
            ctx.save(); ctx.translate(W * cx + shift * (b ? -1 : 1), y); ctx.scale(1, 0.18);
            const g = ctx.createRadialGradient(0, 0, 0, 0, 0, W * rx);
            g.addColorStop(0, `rgba(213,220,230,${0.5 * scene.fog})`); g.addColorStop(1, 'rgba(213,220,230,0)');
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, W * rx, 0, Math.PI * 2); ctx.fill(); ctx.restore();
          }
        }
      }

      /* 🌑 the night pulls in at the edges */
      if (night) {
        const g = ctx.createRadialGradient(W / 2, H * 0.55, Math.min(W, H) * 0.4, W / 2, H * 0.55, Math.max(W, H) * 0.8);
        g.addColorStop(0, 'rgba(2,6,17,0)'); g.addColorStop(1, `rgba(2,6,17,${scene.phase === 'night' ? 0.45 : 0.28})`);
        ctx.globalAlpha = base; ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      }

      /* ⚡ a bolt, a flash and a flicker */
      if (scene.lightning && !still) {
        if (now > nextStrike) { flash = 0.7; flick = true; boltA = 1; bolt = makeBolt(W, H * (overlay ? 0.55 : 0.75)); nextStrike = now + 3800 + Math.random() * 7000; }
        if (bolt && boltA > 0.02) {
          for (const [w, a, col] of [[6, 0.35, '#C9D6FF'], [1.8, 1, '#FFFFFF']]) {
            ctx.globalAlpha = boltA * a; ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineJoin = 'round';
            for (const line of [bolt.pts, bolt.fork]) { ctx.beginPath(); line.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.stroke(); }
          }
          boltA *= 0.86;
        }
        if (flash > 0.01) { ctx.globalAlpha = flash; ctx.fillStyle = '#EEF3FF'; ctx.fillRect(0, 0, W, H); flash *= 0.86; if (flick && flash < 0.12) { flash = 0.45; flick = false; } }
      }
      ctx.globalAlpha = 1;
      if (!still) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [scene, running, overlay]);
  return (
    <div className={`wm-sky ${overlay ? 'over' : ''}`} style={overlay ? undefined : { background: `linear-gradient(180deg, ${scene.colors[0]} 0%, ${scene.colors[1]} 55%, ${scene.colors[2]} 100%)` }}>
      <canvas ref={ref} />
    </div>
  );
}

const lookScene = (look) => {
  const kind = look.weather || 'clear';
  if (!Number.isFinite(look.lat)) return skyScene(fallbackPhase(new Date().getHours()), kind);
  const s = sunAt(Date.now(), look.lat, look.lng);
  return skyScene(skyPhase(s.elevation, s.morning), kind);
};
const isNight = (sc) => sc.phase === 'night' || sc.phase === 'dusk' || sc.phase === 'dawn';

/* the map's own sky (the atmosphere around the globe) in the viewer's colours */
function skySpec(sc) {
  return {
    'sky-color': sc.colors[0], 'horizon-color': sc.colors[2], 'fog-color': sc.colors[1],
    'sky-horizon-blend': 0.6, 'horizon-fog-blend': 0.6, 'fog-ground-blend': 0.4,
    'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 1, 6, 0.6, 10, 0],
  };
}

/* ══ THE DOOR ═══════════════════════════════════════════════════════════ */
export function WorldMapCard({ onOpen }) {
  const lang = lang0();
  const [look] = useState(() => rememberedLook());
  const cells = useMemo(() => cachedCells(), []);
  const box = useRef(null);
  const sky = useMemo(() => lookScene(look), [look]);
  const known = Number.isFinite(look.lat);
  useEffect(() => {
    const el = box.current; if (!el) return undefined;
    let map = null;
    ensureRtl();
    try {
      map = new maplibregl.Map({
        container: el, style: isNight(sky) ? MAP_STYLE_NIGHT : MAP_STYLE_DAY, interactive: false, attributionControl: false,
        center: [known ? look.lng : 30, known ? look.lat : 24], zoom: known ? 3.2 : 1.2,
      });
      map.on('error', () => { if (map && isNight(sky) && !map.isStyleLoaded()) { try { map.setStyle(MAP_STYLE_DAY); } catch { /* gone */ } } });
      map.on('style.load', () => { try { localizeNames(map, lang); } catch { /* own names */ } try { applyLights(map, cityLightLevel(sky.phase), isNight(sky)); } catch { /* own look */ } });
      map.on('load', () => {
        if (!cells) return;
        const pts = mergeCells(cells.cells, 3).slice(0, 400).map((b) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [b.lng, b.lat] }, properties: { color: dotColor(b.o > 0) } }));
        map.addSource('hot', { type: 'geojson', data: { type: 'FeatureCollection', features: pts } });
        map.addLayer({ id: 'hot-glow', type: 'circle', source: 'hot', paint: { 'circle-radius': 9, 'circle-color': ['get', 'color'], 'circle-opacity': 0.3 } });
        map.addLayer({ id: 'hot', type: 'circle', source: 'hot', paint: { 'circle-radius': 4.5, 'circle-color': ['get', 'color'], 'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 1.5 } });
      });
    } catch { map = null; }
    return () => { try { map && map.remove(); } catch { /* gone */ } };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const people = cells ? cells.people : look.people || 0;
  return (
    <button type="button" className="wm-card" onClick={onOpen} aria-label={mapL('world_map', lang)}>
      <div ref={box} className="wm-card-map" />
      <div className="wm-card-wash" style={{ background: `linear-gradient(90deg, ${sky.colors[0]} 0%, ${sky.colors[1]}D9 45%, ${sky.colors[1]}00 100%)` }} />
      <div className="wm-card-words" style={{ color: sky.ink }}>
        <b>🌍 {mapL('world_map', lang)}</b>
        <i>{mapL('world_sub', lang)}</i>
        {people > 0 && <span className="wm-card-count"><span className="wm-live" />{mapL('people_n', lang, { n: compactCount(people, lang) })}{cells && cells.online ? ` · ${mapL('online_now_n', lang, { n: compactCount(cells.online, lang) })}` : ''}</span>}
      </div>
    </button>
  );
}

/* ══ THE MAP ════════════════════════════════════════════════════════════ */
const WX_ICON = { clear: '☀️', partly: '⛅', clouds: '☁️', fog: '🌫️', drizzle: '🌦️', rain: '🌧️', snow: '❄️', storm: '⛈️' };
const fc = (features) => ({ type: 'FeatureCollection', features });
const pt = (lng, lat, properties) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties });

export default function WorldMap({ open, onClose, me, myUid, onProfile }) {
  const lang = lang0();
  const L = (k, v) => mapL(k, lang, v);
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef(new Map());
  const [ready, setReady] = useState(0);
  const [cells, setCells] = useState(() => cachedCells());
  const [mine, setMine] = useState(null);
  const [vis, setVis] = useState('anon');
  const [layer, setLayer] = useState('world');
  const [near, setNear] = useState(null);
  const [friends, setFriends] = useState([]);
  const [weather, setWeather] = useState(null);
  const [skyAt, setSkyAt] = useState(() => { const l = rememberedLook(); return Number.isFinite(l.lat) ? { lat: l.lat, lng: l.lng } : null; });
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [visOpen, setVisOpen] = useState(false);
  const [onSet, setOnSet] = useState('everyone');
  const [profileCity, setProfileCity] = useState(null);
  const [needsCity, setNeedsCity] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityQ, setCityQ] = useState('');
  const [cityHits, setCityHits] = useState(null);
  const [cityBusy, setCityBusy] = useState(false);
  const [toast, setToast] = useState(null);
  /* 🏷 the page I use the map as (I appear as it, and see its audience) — the server's word */
  const [asPage, setAsPageState] = useState(null);
  const [pageView, setPageView] = useState(null);
  const [pageBusy, setPageBusy] = useState(false);
  const [myPages, setMyPages] = useState([]);
  const nearKey = useRef(''); const nearTimer = useRef(0);

  /* ── data ── */
  useEffect(() => {
    if (!open) return undefined;
    let dead = false;
    (async () => {
      const m = await fetchMe();
      if (dead) return;
      if (m && m.ok) {
        setVis(m.vis || 'anon');
        setOnSet(m.on || 'everyone');
        setAsPageState(m.asPage || null);
        setProfileCity(m.profileCity || null);
        if (m.me) { setMine(m.me); setSkyAt({ lat: m.me.lat, lng: m.me.lng }); }
        /* no city on the profile and none chosen: ask for one — nobody is placed without it */
        setNeedsCity(!!m.needsCity);
      }
      const fresh = await loadCells(m && m.cellsUrl);
      if (!dead && fresh) setCells(fresh);
      const f = await fetchFriendsOnMap();
      if (!dead && f && f.ok) setFriends(f.friends || []);
      /* my pages — the ones I can use the map as */
      try {
        const q = await getDocs(query(collection(db, 'pages'), where('ownerUid', '==', myUid)));
        if (!dead) setMyPages(q.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { /* none to choose from */ }
    })();
    return () => { dead = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  /* 🏷 the map as my page sees it */
  useEffect(() => {
    if (!open || !asPage) { setPageView(null); return undefined; }
    let dead = false;
    fetchPageView(asPage.id).then((r) => { if (!dead && r && r.ok) setPageView(r); }).catch(() => {});
    return () => { dead = true; };
  }, [open, asPage && asPage.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!skyAt) return;
    rememberLook({ lat: skyAt.lat, lng: skyAt.lng });
    fetchWeather(skyAt.lat, skyAt.lng).then((w) => { if (w) { setWeather(w); rememberLook({ weather: weatherKind(w.code) }); } }).catch(() => {});
  }, [skyAt && skyAt.lat, skyAt && skyAt.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (cells) rememberLook({ people: cells.people, pages: cells.pages, online: cells.online }); }, [cells]);

  const [tick, setTick] = useState(0);
  useEffect(() => { if (!open) return undefined; const t = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(t); }, [open]);
  const sky = useMemo(() => lookScene({ ...(skyAt || {}), weather: weather ? weatherKind(weather.code) : 'clear' }), [skyAt, weather, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const night = isNight(sky);

  /* ── the camera ── */
  const flyTo = useCallback((lat, lng, zoom, ms) => {
    const m = mapRef.current; if (!m) return;
    m.flyTo({ center: [lng, lat], zoom, speed: 1.1, curve: 1.5, essential: true, ...(ms ? { duration: ms } : {}) });
  }, []);
  const onView = useCallback(() => {
    const m = mapRef.current; if (!m) return;
    const c = m.getCenter(); const z = m.getZoom();
    const l = layerForZoom(z);
    setLayer((x) => (x === l ? x : l));
    if (l !== 'near') return;
    const prefixes = nearPrefixes(c.lat, c.lng);
    const key = prefixes.join(',');
    if (key === nearKey.current) return;
    clearTimeout(nearTimer.current);
    nearTimer.current = setTimeout(async () => {
      nearKey.current = key;
      const r = await fetchNear(prefixes);
      if (r && r.ok && nearKey.current === key) setNear({ key, people: r.people || [], anon: r.anon || [], pages: r.pages || [] });
    }, 320);
  }, []);

  /* ── the data the map draws, as GeoJSON ── */
  const nearParents = useMemo(() => new Set((near ? near.key.split(',') : []).map((g) => g.slice(0, 3))), [near]);
  const cellData = useMemo(() => (asPage && pageView ? fc((layer === 'near' && pageView.near.length ? pageView.near : pageView.cells)
    .map(([gh, n, lat, lng]) => pt(lng, lat, { id: `pv:${gh}`, n, label: compactCount(n, lang), on: false, color: pageKind(asPage.cat).color }))) : fc(cells ? mergeCells(cells.cells, precisionForLayer(layer === 'near' ? 'region' : layer))
    .filter((b) => !(layer === 'near' && nearParents.has(b.gh.slice(0, 3))))
    /* 🟢 green where somebody is online now, slate where everyone is away */
    .map((b) => pt(b.lng, b.lat, { id: b.gh, n: b.n + b.p, label: compactCount(b.n + b.p, lang), on: b.o > 0, color: b.n === 0 ? '#FFB020' : dotColor(b.o > 0) })) : [])), [cells, layer, nearParents, lang, asPage, pageView]);
  const nearData = useMemo(() => {
    if (asPage && pageView) return fc(pageView.pages.map((x) => pt(x.lng, x.lat, { kind: 'page', id: x.id, cat: x.cat || '', color: pageKind(x.cat).color })));
    if (!near || layer !== 'near') return fc([]);
    const fids = new Set(friends.map((f) => f.uid));
    return fc([
      ...near.anon.map((a) => { const at = a.exact ? { lat: a.lat, lng: a.lng } : jitterInCell(a.gh); return pt(at.lng, at.lat, { kind: 'anon', id: a.gh, n: a.n, on: a.o > 0, color: dotColor(a.o > 0) }); }),
      /* a city's people share its centre — each is drawn at a fixed spot of their own around it */
      ...near.people.filter((x) => x.id !== myUid && !fids.has(x.id)).map((x) => { const at = spreadAround(x.lat, x.lng, x.id); return pt(at.lng, at.lat, { kind: 'person', id: x.id, on: !!x.online, color: dotColor(!!x.online) }); }),
      /* a page its owner appears as is a face (a marker, below), not a pin */
      ...near.pages.filter((x) => !x.face && !(asPage && x.id === asPage.id)).map((x) => pt(x.lng, x.lat, { kind: 'page', id: x.id, cat: x.cat || '', color: pageKind(x.cat).color })),
    ]);
  }, [near, layer, friends, myUid, asPage, pageView]);
  const dataRef = useRef({}); dataRef.current = { cellData, nearData };

  /* ── the map: made on open, removed on close ── */
  useEffect(() => {
    if (!open || !boxRef.current) return undefined;
    const look = rememberedLook();
    let map;
    ensureRtl();
    try {
      map = new maplibregl.Map({
        container: boxRef.current,
        style: night ? MAP_STYLE_NIGHT : MAP_STYLE_DAY,
        center: [Number.isFinite(look.lng) ? look.lng : 30, 20],
        zoom: MAP_START_ZOOM,
        attributionControl: { compact: true },
        maxPitch: 70,
      });
    } catch { return undefined; }
    mapRef.current = map;
    let triedDay = false;
    map.on('error', () => { if (!triedDay && !map.isStyleLoaded()) { triedDay = true; try { map.setStyle(MAP_STYLE_DAY, { diff: false }); } catch { /* gone */ } } });
    /* every style (day, night) is a fresh canvas: the globe, the sky and our layers go back on */
    map.on('style.load', () => {
      try { map.setProjection({ type: 'globe' }); } catch { /* an older engine: flat is fine */ }
      try { map.setSky(skySpec(skyRef.current)); } catch { /* no sky support */ }
      try { localizeNames(map, lang); } catch { /* the style's own names */ }
      addLayers(map);
      try { applyLights(map, cityLightLevel(skyRef.current.phase), isNight(skyRef.current)); } catch { /* the style's own look */ }
      setReady((x) => x + 1);
    });
    map.on('moveend', onView);
    map.once('load', () => {
      onView();
      if (Number.isFinite(look.lat)) setTimeout(() => flyTo(look.lat, look.lng, MAP_HOME_ZOOM, 2600), 300);
    });
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      markers.current.forEach((mk) => mk.remove()); markers.current.clear();
      try { map.remove(); } catch { /* gone */ }
      mapRef.current = null;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const skyRef = useRef(sky); skyRef.current = sky;
  /* night ↔ day swaps the style; the sky colours follow the hour */
  const styleNight = useRef(night);
  useEffect(() => {
    const m = mapRef.current; if (!m) return;
    if (styleNight.current !== night) { styleNight.current = night; try { m.setStyle(night ? MAP_STYLE_NIGHT : MAP_STYLE_DAY, { diff: false }); } catch { /* gone */ } return; }
    try { m.setSky(skySpec(sky)); } catch { /* no sky support */ }
  }, [night, sky]);
  /* 🌃 the cities light up as the evening comes */
  const lightLevel = Math.round(cityLightLevel(sky.phase) * 20) / 20;
  useEffect(() => {
    const m = mapRef.current; if (!m || !m.isStyleLoaded()) return;
    try { applyLights(m, lightLevel, night); } catch { /* the style's own look */ }
  }, [lightLevel, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  function addLayers(map) {
    const d = dataRef.current;
    if (!map.getSource('wm-cells')) map.addSource('wm-cells', { type: 'geojson', data: d.cellData || fc([]) });
    if (!map.getSource('wm-near')) map.addSource('wm-near', { type: 'geojson', data: d.nearData || fc([]) });
    /* 🟥 THE COUNTS' WORDS HAVE A SOURCE OF THEIR OWN. Text needs the style's fonts;
       if they ever fail to load, the whole source fails with them — and the
       bubbles would vanish for the sake of their numbers. */
    if (!map.getSource('wm-cells-text')) map.addSource('wm-cells-text', { type: 'geojson', data: d.cellData || fc([]) });
    const r = ['interpolate', ['linear'], ['ln', ['+', 1, ['get', 'n']]], 0, 12, 14, 30];
    /* only crowds with somebody online breathe */
    map.addLayer({ id: 'wm-cells-pulse', type: 'circle', source: 'wm-cells', filter: ['==', ['get', 'on'], true], paint: { 'circle-radius': r, 'circle-color': ['get', 'color'], 'circle-opacity': 0.25, 'circle-blur': 0.4 } });
    map.addLayer({ id: 'wm-cells', type: 'circle', source: 'wm-cells', paint: { 'circle-radius': r, 'circle-color': ['get', 'color'], 'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 2.5 } });
    map.addLayer({ id: 'wm-cells-count', type: 'symbol', source: 'wm-cells-text', layout: { 'text-field': ['get', 'label'], 'text-font': FONT, 'text-size': 12, 'text-allow-overlap': true }, paint: { 'text-color': '#04120B' } });
    map.addLayer({ id: 'wm-anon', type: 'circle', source: 'wm-near', filter: ['==', ['get', 'kind'], 'anon'],
      paint: { 'circle-radius': ['interpolate', ['linear'], ['ln', ['+', 1, ['get', 'n']]], 0, 6, 6, 12], 'circle-color': ['get', 'color'], 'circle-stroke-color': 'rgba(4,40,24,0.55)', 'circle-stroke-width': 2 } });
    map.addLayer({ id: 'wm-person', type: 'circle', source: 'wm-near', filter: ['==', ['get', 'kind'], 'person'],
      paint: { 'circle-radius': 8, 'circle-color': ['get', 'color'], 'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 3 } });
    map.addLayer({ id: 'wm-page', type: 'circle', source: 'wm-near', filter: ['==', ['get', 'kind'], 'page'],
      paint: { 'circle-radius': 10, 'circle-color': ['get', 'color'], 'circle-stroke-color': '#FFFFFF', 'circle-stroke-width': 2.5 } });
    /* layer listeners belong to the MAP and outlive a style swap — bind them once */
    if (map.__wmBound) return;
    map.__wmBound = true;
    for (const id of ['wm-cells', 'wm-anon', 'wm-person', 'wm-page']) {
      map.on('click', id, (e) => { const f = e.features && e.features[0]; if (f) tapFeature(id, f); });
      map.on('mouseenter', id, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; });
    }
  }

  /* data → the map, as it changes */
  useEffect(() => {
    const m = mapRef.current; if (!m) return;
    for (const id of ['wm-cells', 'wm-cells-text']) { const s = m.getSource(id); if (s) s.setData(cellData); }
  }, [cellData, ready]);
  useEffect(() => { const s = mapRef.current && mapRef.current.getSource('wm-near'); if (s) s.setData(nearData); }, [nearData, ready]);

  /* the pulse: the halo behind every count breathes out and fades */
  useEffect(() => {
    if (!open) return undefined;
    let raf = 0; let last = 0;
    const step = (t) => {
      raf = requestAnimationFrame(step);
      if (t - last < 50) return; last = t;
      const m = mapRef.current; if (!m || !m.getLayer('wm-cells-pulse')) return;
      const k = (t % 2000) / 2000;
      try {
        m.setPaintProperty('wm-cells-pulse', 'circle-radius', ['*', 1 + k * 0.9, ['interpolate', ['linear'], ['ln', ['+', 1, ['get', 'n']]], 0, 12, 14, 30]]);
        m.setPaintProperty('wm-cells-pulse', 'circle-opacity', 0.45 * (1 - k));
      } catch { /* style swapping */ }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  /* friends and me: faces, as markers */
  useEffect(() => {
    const m = mapRef.current; if (!m) return;
    const want = new Map();
    if (asPage && pageView) {
      /* 🏷 the map as my page: the page itself, by its face */
      if (Number.isFinite(asPage.lat)) want.set('self', { kind: 'page', ...{ lat: asPage.lat, lng: asPage.lng }, name: asPage.name, image: asPage.image, color: pageKind(asPage.cat).color, on: true, data: { id: asPage.id, page: true } });
    } else {
      for (const f of friends.slice(0, 200)) { const at = spreadAround(f.lat, f.lng, f.uid); want.set(`f:${f.uid}`, { kind: 'friend', ...at, name: (f.name || '').split(' ')[0], image: f.image, on: !!f.online, data: f }); }
      /* pages their owners appear as wear their logo, lit while the owner is online */
      if (near && layer === 'near') for (const x of near.pages) if (x.face && !(asPage && x.id === asPage.id)) want.set(`pf:${x.id}`, { kind: 'page', lat: x.lat, lng: x.lng, name: x.face.name, image: x.face.image, color: pageKind(x.cat).color, on: !!x.online, data: { id: x.id, page: true } });
      if (asPage && Number.isFinite(asPage.lat)) want.set('self', { kind: 'page', lat: asPage.lat, lng: asPage.lng, name: asPage.name, image: asPage.image, color: pageKind(asPage.cat).color, on: true, data: { id: asPage.id, page: true } });
      else if (mine && vis !== 'nobody') { const at = spreadAround(mine.lat, mine.lng, myUid); want.set('self', { kind: 'self', ...at, name: L('you'), image: me && me.profileImage, on: true, data: { id: myUid } }); }
    }
    for (const [id, mk] of markers.current) if (!want.has(id)) { mk.remove(); markers.current.delete(id); }
    for (const [id, d] of want) {
      /* 🟥 a face whose picture arrived late kept its letter: a new look is a new face */
      const look = [d.kind, d.image || '', d.name || '', d.color || ''].join('|');
      const old = markers.current.get(id);
      if (old && old.__look !== look) { old.remove(); markers.current.delete(id); }
      else if (old) { old.setLngLat([d.lng, d.lat]); old.getElement().classList.toggle('online', !!d.on); continue; }
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `wm-face ${d.kind}${d.on ? ' online' : ''}`;
      if (d.color) el.style.setProperty('--wm-ring', d.color);
      const pic = document.createElement('span'); pic.className = 'wm-face-pic';
      if (d.image) { const img = document.createElement('img'); img.src = d.image; img.alt = ''; pic.appendChild(img); } else pic.textContent = (d.name || '?').charAt(0).toUpperCase();
      const nm = document.createElement('span'); nm.className = 'wm-face-name'; nm.textContent = d.name || '';
      const dot = document.createElement('span'); dot.className = 'wm-face-on';
      el.appendChild(pic); el.appendChild(dot); el.appendChild(nm);
      el.addEventListener('click', (e) => { e.stopPropagation(); tapDot({ kind: d.kind, lat: d.lat, lng: d.lng, data: d.data }); });
      const mk = new maplibregl.Marker({ element: el, anchor: 'top', offset: [0, -24] }).setLngLat([d.lng, d.lat]).addTo(m);
      mk.__look = look;
      markers.current.set(id, mk);
    }
  }, [friends, mine, vis, ready, near, layer, asPage, pageView, me && me.profileImage]); // eslint-disable-line react-hooks/exhaustive-deps

  /* a tap on the map itself, away from everything, puts the cards away */
  useEffect(() => {
    const m = mapRef.current; if (!m) return undefined;
    const onClick = (e) => {
      const hit = m.queryRenderedFeatures(e.point, { layers: ['wm-cells', 'wm-anon', 'wm-person', 'wm-page'].filter((x) => m.getLayer(x)) });
      if (!hit.length) { setCard(null); setVisOpen(false); }
    };
    m.on('click', onClick);
    return () => { try { m.off('click', onClick); } catch { /* gone */ } };
  }, [ready]);

  const say = (t) => { setToast(t); setTimeout(() => setToast(null), 2200); };
  const tapFeature = (layerId, f) => {
    const [lng, lat] = f.geometry.coordinates; const p = f.properties || {};
    if (layerId === 'wm-cells') return tapDot({ kind: 'cell', lat, lng, data: p });
    if (layerId === 'wm-anon') return tapDot({ kind: 'anon', lat, lng, data: { n: Number(p.n) || 1 } });
    if (layerId === 'wm-page') return tapDot({ kind: 'page', lat, lng, data: { id: p.id } });
    return tapDot({ kind: 'person', lat, lng, data: { id: p.id } });
  };
  const tapDot = async (d) => {
    const m = mapRef.current; const z = m ? m.getZoom() : MAP_WORLD_ZOOM;
    if (d.kind === 'cell' && String((d.data && d.data.id) || '').startsWith('pv:')) { say(L('page_followers_n', { n: compactCount(Number(d.data.n) || 0, lang) })); flyTo(d.lat, d.lng, diveFrom(layerForZoom(z))); return; }
    if (d.kind === 'cell') { flyTo(d.lat, d.lng, diveFrom(layerForZoom(z))); return; }
    if (d.kind === 'anon') { say(L('someone_here', { n: compactCount(d.data.n || 1, lang) })); if (z < 15) flyTo(d.lat, d.lng, z + 2, 700); return; }
    /* 🟢 my own face opens MY card from what is already known — never "private" to myself */
    if (d.kind === 'self') {
      setCard({ kind: 'person', relation: 'self', uid: myUid, name: (me && (me.fullName || me.username)) || '', image: (me && me.profileImage) || '', city: mine ? mine.city : '', region: mine ? mine.region : '', country: mine ? mine.country : '', online: true, lat: d.lat, lng: d.lng });
      return;
    }
    const kind = d.kind === 'page' ? 'page' : 'person';
    const id = d.kind === 'page' ? d.data.id : (d.data.uid || d.data.id);
    setCard({ loading: true });   // (a page drawn by its face opens the page's card too)
    const r = await fetchWho(id, kind);
    setCard(r && r.ok ? { ...r, lat: d.lat, lng: d.lng } : { error: true });
  };
  const act = async () => {
    if (!card || card.loading || card.error || busy) return;
    setBusy(true);
    try {
      if (card.kind === 'page') {
        const r = await toggleFollowPage(card.id, myUid, !!card.following);
        if (r && r.ok) setCard((c) => ({ ...c, following: !c.following, followers: Math.max(0, (c.followers || 0) + (c.following ? -1 : 1)) }));
      } else if (card.relation === 'none') {
        const r = await sendFriendRequest(me, { uid: card.uid, name: card.name });
        if (r === true) setCard((c) => ({ ...c, relation: 'sent' }));
      }
    } catch { /* the card keeps its button */ } finally { setBusy(false); }
  };
  const openFull = () => {
    if (!card || card.error) return;
    onClose();
    if (card.kind === 'page') onProfile({ pageId: card.id, name: card.name, image: card.image });
    else onProfile({ uid: card.uid, name: card.name, image: card.image });
  };
  /* ── my city ── */
  useEffect(() => {
    if (!cityOpen) return undefined;
    const q = cityQ.trim();
    if (q.length < 2) { setCityHits(null); return undefined; }
    const t = setTimeout(async () => { const r = await searchCities(q, lang); if (r && r.ok) setCityHits(r.hits || []); }, 350);
    return () => clearTimeout(t);
  }, [cityQ, cityOpen, lang]);
  const placed = async (r) => {
    if (r && r.ok && r.me) {
      setMine(r.me); setSkyAt({ lat: r.me.lat, lng: r.me.lng }); setNeedsCity(false);
      setCityOpen(false); setCityQ(''); setCityHits(null);
      flyTo(r.me.lat, r.me.lng, MAP_CITY_ZOOM);
      const fresh = await loadCells(null); if (fresh) setCells(fresh);
    } else if (r && r.ok && r.removed) {
      /* 🟥 somebody set to No one is kept off the map ({ removed: true }) — that is the setting, not a failure */
      say(L('err_hidden'));
    } else {
      if (r && !r.ok) console.warn('🌍 [map] place refused:', r.status, r.error);
      say(L(mapErrorKey(r)));   // 🟢 why, not a shrug
    }
  };
  const pickCity = async (c) => { if (cityBusy) return; setCityBusy(true); try { await placed(await chooseCity(c.id)); } finally { setCityBusy(false); } };
  const pickProfileCity = async () => { if (cityBusy) return; setCityBusy(true); try { await placed(await placeFromProfileCity()); } finally { setCityBusy(false); } };
  /* 🟥 the server's answer is what the screen shows — a setting it did not take must not look taken */
  const settle = async (r) => {
    const m = await fetchMe();
    if (m && m.ok) { setVis(m.vis || 'anon'); setOnSet(m.on || 'everyone'); setMine(m.me || null); setAsPageState(m.asPage || null); }
    if (!r || !r.ok) say(L(r && r.error ? mapErrorKey(r) : 'not_saved'));
  };
  const chooseOnline = async (v) => {
    setOnSet(v);
    try { await updateDoc(doc(db, 'users', myUid), { 'privacySettings.mapOnline': v }); } catch { await settle(null); return; }
    await settle(await refreshAfterSetting());
  };
  const chooseVis = async (v) => {
    setVis(v); setVisOpen(false);
    if (v === 'nobody') setMine(null);
    try { await updateDoc(doc(db, 'users', myUid), { 'privacySettings.mapVisibility': v }); } catch { await settle(null); return; }
    await settle(await refreshAfterSetting());
  };
  /* 🏷 use the map as one of my pages (or as me again) — one switch */
  const switchAs = async (pageId) => {
    if (pageBusy || (pageId || null) === (asPage ? asPage.id : null)) return;
    setPageBusy(true);
    try {
      const r = await setAsPage(pageId);
      if (r && r.ok) {
        setAsPageState(r.asPage || null);
        if (r.asPage && Number.isFinite(r.asPage.lat)) flyTo(r.asPage.lat, r.asPage.lng, MAP_HOME_ZOOM, 1400);
      } else say(L(r && r.error === 'page_not_placed' ? 'page_not_placed' : mapErrorKey(r)));
    } finally { setPageBusy(false); }
  };

  if (!open) return null;
  const kmTo = (c) => (mine && Number.isFinite(c.lat) ? Math.round(distanceKm(mine.lat, mine.lng, c.lat, c.lng)) : null);
  return (
    <div className="wm" role="dialog" aria-label={L('world_map')}>
      <div ref={boxRef} className="wm-map" />
      <SkyCanvas scene={sky} running={open} overlay />
      <div className="wm-skyband" style={{ background: `linear-gradient(180deg, ${sky.colors[0]} 0%, ${sky.colors[1]}CC 55%, ${sky.colors[1]}00 100%)` }} />

      <header className="wm-top" style={{ color: sky.ink }}>
        <button type="button" className="wm-glass wm-x" onClick={onClose} aria-label="Close">✕</button>
        <div className="wm-titles">
          <b>{asPage ? L('page_view_title', { page: asPage.name }) : L('world_map')}</b>
          {asPage ? <i>{pageView ? `${L('page_followers_n', { n: compactCount(pageView.placed, lang) })} · ${L('page_same_kind_n', { n: compactCount(pageView.pages.length, lang) })}` : L('loading_world')}</i> : <i>{cells ? `${L('people_n', { n: compactCount(cells.people, lang) })}${cells.online ? ` · ● ${L('online_now_n', { n: compactCount(cells.online, lang) })}` : ''}` : L('loading_world')}</i>}
        </div>
        <span className="wm-glass wm-wx">{WX_ICON[sky.weather] || '☀️'}{night && sky.weather === 'clear' ? '🌙' : ''} {weather && Number.isFinite(weather.temp) ? `${formatTemp(weather.temp, usesFahrenheit(mine && mine.cc, typeof navigator !== 'undefined' ? navigator.language : ''))} · ` : ''}{L(`ph_${sky.phase}`)}</span>
      </header>
      {toast && <div className="wm-glass wm-toast">{toast}</div>}
      {asPage && !toast && <button type="button" className="wm-glass wm-toast wm-back" onClick={() => switchAs(null)}>👤 {L('back_to_me')}</button>}

      <div className="wm-fabs">
        <button type="button" className="wm-glass" onClick={() => (mine ? flyTo(mine.lat, mine.lng, MAP_CITY_ZOOM) : setCityOpen(true))} title={L('my_location')}>➤</button>
        <button type="button" className="wm-glass" onClick={() => { const m = mapRef.current; const c = m && m.getCenter(); flyTo(c ? c.lat : 20, c ? c.lng : 30, MAP_WORLD_ZOOM, 1600); }} title={L('world_map')}>🌐</button>
        <button type="button" className="wm-glass" onClick={() => mapRef.current && mapRef.current.zoomIn()} title="Zoom in">＋</button>
        <button type="button" className="wm-glass" onClick={() => mapRef.current && mapRef.current.zoomOut()} title="Zoom out">－</button>
      </div>

      <footer className="wm-bottom">
        {friends.length > 0 && !asPage && (
          <div className="wm-friends">
            {friends.map((f) => (
              <button type="button" key={f.uid} onClick={() => flyTo(f.lat, f.lng, MAP_CITY_ZOOM)} title={`${f.name} · ${L(f.online ? 'online' : 'offline')}`} className={f.online ? 'online' : ''}>
                {f.image ? <img src={f.image} alt="" /> : <span>{(f.name || '?').charAt(0).toUpperCase()}</span>}
                {f.online && <em className="wm-on-badge" />}
                <i>{(f.name || '').split(' ')[0]}</i>
              </button>
            ))}
          </div>
        )}
        <button type="button" className="wm-glass wm-vis" onClick={() => setVisOpen((x) => !x)}>
          <span className={`wm-vis-dot ${vis}`} /> {L('who_sees')}: {L(`vis_${vis}`)} {visOpen ? '▾' : '▴'}
        </button>
      </footer>

      {visOpen && (
        <div className="wm-sheet">
          <b>{L('my_place')}</b>
          <button type="button" className="wm-place-row" onClick={() => { setVisOpen(false); setCityOpen(true); }}>
            <span>📍</span>
            <span><b>{mine ? placeLabel({ name: mine.city, region: mine.region, country: mine.country }) : L('add_city')}</b>{mine && mine.src === 'profile' && <i>{L('from_profile')}</i>}</span>
            <em>{L(mine ? 'change' : 'add_city')}</em>
          </button>
          {myPages.length > 0 && (
            <>
              <b className="wm-sheet-h">{L('view_as')}</b>
              <button type="button" className={`wm-vis-row ${!asPage ? 'on' : ''}`} onClick={() => switchAs(null)}>
                {me && me.profileImage ? <img className="wm-as-pic" src={me.profileImage} alt="" /> : <span className="wm-as-pic">👤</span>}
                <span><b>{L('view_as_me')}</b></span>
                {!asPage && <em>✓</em>}
              </button>
              {myPages.slice(0, 10).map((pg) => (
                <button type="button" key={pg.id} className={`wm-vis-row ${asPage && asPage.id === pg.id ? 'on' : ''}`} onClick={() => switchAs(pg.id)} disabled={pageBusy}>
                  {pg.profileImage ? <img className="wm-as-pic page" style={{ borderColor: pageKind(pg.category).color }} src={pg.profileImage} alt="" /> : <span className="wm-as-pic page" style={{ background: pageKind(pg.category).color }} />}
                  <span><b>{L('appear_as_page', { page: pg.name || '' })}</b><i>{L('appear_as_page_sub')}</i></span>
                  {asPage && asPage.id === pg.id && <em>✓</em>}
                </button>
              ))}
            </>
          )}
          <b className="wm-sheet-h">{L('who_sees')}</b>
          {MAP_VISIBILITIES.map((v) => (
            <button type="button" key={v} className={`wm-vis-row ${vis === v ? 'on' : ''}`} onClick={() => chooseVis(v)}>
              <span className={`wm-vis-dot ${v}`} />
              <span><b>{L(`vis_${v}`)}</b><i>{L(`vis_${v}_sub`)}</i></span>
              {vis === v && <em>✓</em>}
            </button>
          ))}
          <b className="wm-sheet-h">{L('show_online')}</b>
          {MAP_ONLINE_CHOICES.map((v) => (
            <button type="button" key={v} className={`wm-vis-row ${onSet === v ? 'on' : ''}`} onClick={() => chooseOnline(v)}>
              <span className="wm-vis-dot" style={{ background: v === 'nobody' ? MAP_OFFLINE : MAP_GREEN, opacity: v === 'friends' ? 0.75 : 1 }} />
              <span><b>{L(`on_${v}`)}</b><i>{L(`on_${v}_sub`)}</i></span>
              {onSet === v && <em>✓</em>}
            </button>
          ))}
          <small>{L('location_why')}</small>
        </div>
      )}

      {cityOpen && (
        <div className="wm-city">
          <div className="wm-city-h"><b>{L('my_place')}</b><button type="button" className="wm-ask-x" onClick={() => { setCityOpen(false); setCityQ(''); setCityHits(null); }} aria-label="Close">✕</button></div>
          <div className="wm-search">
            <span>🔍</span>
            <input value={cityQ} onChange={(e) => setCityQ(e.target.value)} placeholder={L('search_city')} autoFocus />
            {cityBusy && <span className="wm-spin sm" />}
          </div>
          {profileCity && !(mine && mine.src === 'profile') && (
            <button type="button" className="wm-city-row" onClick={pickProfileCity}><span>👤</span><b>{L('use_profile_city', { city: placeLabel(profileCity) })}</b></button>
          )}
          {cityHits && !cityHits.length && <small>{L('no_cities')}</small>}
          {(cityHits || []).slice(0, 8).map((c) => (
            <button type="button" key={c.id} className="wm-city-row" onClick={() => pickCity(c)}>
              <span>📍</span><span><b>{c.name}</b><i>{[c.region, c.country].filter(Boolean).join(', ')}</i></span>
            </button>
          ))}
          <small>{L('location_why')}</small>
        </div>
      )}

      {needsCity && !mine && !visOpen && !cityOpen && (
        <div className="wm-ask">
          <span>📍</span>
          <div><b>{L('add_city_title')}</b><i>{L('add_city_sub')}</i></div>
          <button type="button" className="wm-go" onClick={() => setCityOpen(true)} aria-label={L('add_city')}>＋</button>
          <button type="button" className="wm-ask-x" onClick={() => setNeedsCity(false)} aria-label="Close">✕</button>
        </div>
      )}

      {card && (
        <div className="wm-card-sheet">
          {card.loading ? <div className="wm-spin" /> : card.error ? (
            <p className="wm-private">{L('private_dot')}</p>
          ) : (
            <>
              <div className="wm-who">
                {card.image ? <img src={card.image} alt="" /> : <span>{(card.name || '?').charAt(0).toUpperCase()}</span>}
                <div>
                  <b>{card.relation === 'self' ? `${card.name} · ${L('you')}` : card.name}</b>
                  {card.kind === 'page' ? (
                    <i><em className="wm-kind" style={{ background: pageKind(card.category).color }}>{L(pageKind(card.category).key)}</em> {L('followers_n', { n: compactCount(card.followers || 0, lang) })}{card.city ? ` · ${card.city}` : ''}</i>
                  ) : (
                    <i>{placeLabel({ name: card.city, region: card.region, country: card.country })}{card.relation !== 'self' && kmTo(card) != null ? ` · ${kmTo(card) < 2 ? L('near_you') : L('km_away', { n: compactCount(kmTo(card), lang) })}` : ''}</i>
                  )}
                  {card.kind === 'person' && card.relation !== 'self' && (
                    <i className={`wm-online ${card.online ? 'on' : ''}`}><span />{L(card.online ? 'online' : 'offline')}</i>
                  )}
                </div>
              </div>
              <div className="wm-actions">
                {card.kind === 'page' ? (!card.mine && (
                  <button type="button" className={`wm-primary ${card.following ? 'off' : ''}`} onClick={act} disabled={busy}>{L(card.following ? 'following' : 'follow')}</button>
                )) : card.relation !== 'self' && (
                  <button type="button" className={`wm-primary ${card.relation === 'sent' || card.relation === 'friend' ? 'off' : ''}`} onClick={act}
                    disabled={busy || card.relation === 'sent' || card.relation === 'friend' || card.relation === 'received'}>
                    {L(card.relation === 'friend' ? 'friends' : card.relation === 'sent' ? 'requested' : card.relation === 'received' ? 'respond' : 'add_friend')}
                  </button>
                )}
                <button type="button" className="wm-secondary" onClick={openFull}>{L(card.kind === 'page' ? 'view_page' : 'view_profile')}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
